/**
 * accessToken — client-side ed25519 access-token verification for the gated
 * pages (/claude-masterclass, /claude-workshop-replay).
 *
 * The server (api/stripe/*-verify-email.ts) verifies a buyer against Stripe
 * ONCE and mints a short-lived, ed25519-signed token. The browser stores that
 * token and, on repeat visits within the token window, verifies it locally with
 * the public key — unlocking with ZERO server calls and ZERO Stripe calls.
 *
 * Security model:
 *  - Verification is offline: signature (ed25519) + expiry (`exp`) + `scope`.
 *  - The `scope` claim is enforced so a masterclass token can never unlock the
 *    workshop and vice versa, even though the two gates also use distinct
 *    storage keys.
 *  - Fail OPEN to the old behavior, never open on access: if the public key env
 *    var is missing, or the token is absent/expired/forged/wrong-scope,
 *    `verifyAccessToken` returns null and the caller falls back to the existing
 *    server-verify-every-visit flow. A missing key degrades to the working
 *    paywall; it never unlocks content on its own.
 *
 * Token format mirrors api/stripe/_lib.ts signJwt exactly:
 *   base64url(header).base64url(payload).base64url(signature)
 * where header = { alg: "EdDSA", typ: "JWT" }, the signed message is the UTF-8
 * bytes of `header.payload`, and payload = { scope, email, iat, exp }.
 */

import * as ed from "@noble/ed25519";

// Configure @noble/ed25519 to hash with the platform WebCrypto SHA-512. The
// installed dependency is v3, where the async hook is `hashes.sha512Async`
// (v2 used `etc.sha512Async`); v3 ships a WebCrypto default, but we set it
// explicitly (and also the v2-style hook) to match api/stripe/_lib.ts and to
// stay correct if the dep is ever pinned back to v2. `crypto.subtle` exists in
// browsers and in the Node used for SSR/prerender, and this is only assignment
// at module load — the hash is never invoked during SSR.
const sha512Async = async (message: Uint8Array): Promise<Uint8Array> => {
  const { subtle } = globalThis.crypto;
  const hash = await subtle.digest("SHA-512", message);
  return new Uint8Array(hash);
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const edAny = ed as any;
// Only assign when the target object is EXTENSIBLE. @noble/ed25519 v3 freezes
// its `etc` (and may freeze `hashes`) object, so a bare `edAny.etc.sha512Async =`
// throws `TypeError: Cannot add property sha512Async, object is not extensible`
// at module load — which takes down the whole client bundle (blank app) and the
// SSR/sitemap build under newer Node runtimes (seen on Node 25). Skipping the
// assignment when frozen is safe: v3 already ships a WebCrypto SHA-512 default,
// so the hook simply isn't needed there. On v2 (or any build where the objects
// are writable) the explicit assignment still happens exactly as before.
if (edAny.hashes && Object.isExtensible(edAny.hashes)) edAny.hashes.sha512Async = sha512Async;
if (edAny.etc && Object.isExtensible(edAny.etc)) edAny.etc.sha512Async = sha512Async;

export type AccessScope = "masterclass" | "workshop";

export interface AccessTokenClaims {
  scope: AccessScope;
  email: string;
  iat: number;
  exp: number;
}

function publicKeyHex(): string | null {
  const hex = import.meta.env.VITE_ED25519_PUBLIC_KEY_HEX;
  if (!hex) return null;
  const normalized = hex.trim().toLowerCase();
  // 32-byte ed25519 public key = 64 hex chars. Reject anything malformed rather
  // than letting hexToBytes produce garbage.
  if (!/^[0-9a-f]{64}$/.test(normalized)) return null;
  return normalized;
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function base64urlDecodeToBytes(input: string): Uint8Array {
  const padded = input
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(input.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function tokenStorageKey(storageKey: string): string {
  return `${storageKey}_token`;
}

/**
 * Verify a token string fully client-side and return its claims, or null if it
 * is missing/malformed/expired/forged or does not match `expectedScope`. Never
 * throws. Returns null (→ caller falls back to server verify) when the public
 * key env var is absent.
 */
export async function verifyAccessToken(
  token: string | null | undefined,
  expectedScope: AccessScope,
): Promise<AccessTokenClaims | null> {
  try {
    if (!token || typeof token !== "string") return null;

    const pubHex = publicKeyHex();
    if (!pubHex) return null; // no key exposed → fall back to server verify

    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const [headerB64, payloadB64, sigB64] = parts;
    if (!headerB64 || !payloadB64 || !sigB64) return null;

    const header = JSON.parse(
      new TextDecoder().decode(base64urlDecodeToBytes(headerB64)),
    ) as { alg?: string };
    if (header.alg !== "EdDSA") return null;

    // Verify signature over `header.payload` before trusting any claim.
    const message = new TextEncoder().encode(`${headerB64}.${payloadB64}`);
    const signature = base64urlDecodeToBytes(sigB64);
    const publicKey = hexToBytes(pubHex);
    const ok = await ed.verifyAsync(signature, message, publicKey);
    if (!ok) return null;

    const claims = JSON.parse(
      new TextDecoder().decode(base64urlDecodeToBytes(payloadB64)),
    ) as Partial<AccessTokenClaims>;

    // Scope must match cryptographically — this is what prevents a masterclass
    // token from unlocking the workshop and vice versa.
    if (claims.scope !== expectedScope) return null;

    if (typeof claims.exp !== "number") return null;
    const nowSec = Math.floor(Date.now() / 1000);
    if (nowSec >= claims.exp) return null; // expired

    if (typeof claims.email !== "string" || !claims.email.includes("@")) {
      return null;
    }
    if (typeof claims.iat !== "number") return null;

    return {
      scope: claims.scope,
      email: claims.email,
      iat: claims.iat,
      exp: claims.exp,
    };
  } catch {
    return null;
  }
}

/** Read the stored token string for a gate (or null). Safe on the server. */
export function readStoredToken(storageKey: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(tokenStorageKey(storageKey));
  } catch {
    return null;
  }
}

/** Persist a freshly minted token for a gate. */
export function storeToken(storageKey: string, token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(tokenStorageKey(storageKey), token);
  } catch {
    // ignore (Safari private mode, etc.)
  }
}

/** Remove the stored token for a gate (used on sign-out and on denial). */
export function clearStoredToken(storageKey: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(tokenStorageKey(storageKey));
  } catch {
    // ignore
  }
}
